<template>
  <div
    v-if="isVisible || showDisabledIfNotVisible"
    :disabled="isDisabled"
    :class="wrapperClass"
    @click="handleClick"
    v-bind="$attrs">
    <slot></slot>
  </div>
</template>

<script setup lang="ts">
  import { computed, toRefs, defineProps } from "vue";
  import { usePermissionsAccess } from "@/composables/usePermissionsAccess";
  import { Permissions, PermissionLevels } from "@/models/permissions";

  const props = defineProps({
    permissionIds: {
      type: Array as () => Permissions[],
      default: () => [],
    },
    permissionLevel: {
      type: Number as () => PermissionLevels,
      default: PermissionLevels.None,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showDisabledIfNotVisible: {
      // shows if not visible but component is disabled
      type: Boolean,
      default: false,
    },
  });

  const { permissionIds, permissionLevel, disabled, showDisabledIfNotVisible } =
    toRefs(props);

  const emit = defineEmits(["click"]);

  const handleClick = (event) => {
    emit("click", event);
  };
                
  const permissionsAccess = usePermissionsAccess();

  const isVisible = computed(() => {
    const permissionIdsValue = permissionIds?.value || [];
    return (
      permissionIdsValue.length === 0 ||
      permissionsAccess.hasPermission(permissionIdsValue, permissionLevel.value)
    );
  });

  const isDisabled = computed(() => {
    return (
      disabled.value || (!isVisible.value && showDisabledIfNotVisible.value)
    );
  });

  const wrapperClass = computed(() => ({
    "permission-wrapper": true,
    "disable-clicks": isDisabled.value,
  }));
</script>

<style scoped>
  .permission-wrapper {
    pointer-events: auto;
  }
  .permission-wrapper.disable-clicks {
    pointer-events: none;
    opacity: 0.5;
    cursor: not-allowed;
  }

</style>
